import { Box, Divider, Text, usePortal } from '@talos/kyoko';
import { PriceGridFavoriteFilterButton } from '../../PriceGridFavoriteFilterButton';
import { PriceGridHeaderActions, PriceGridHeaderContainer, PriceGridHeaderFilters } from '../../PriceGridHeaderLayout';
import { PriceGridLadderControls } from '../../PriceGridLadderControls';
import { PriceGridPrimingControls } from '../../PriceGridPrimingControls';
import { PriceGridProductTypeFilter, PriceGridSymbolFilters, usePriceGridFilterControls } from '../../filters';
import type { PriceGridLadderType } from '../../ladder';
import type { PriceGridFilterState, PriceGridHeaderParams } from '../../types';

interface GenericPriceGridHeaderProps extends PriceGridHeaderParams {
  filterState: PriceGridFilterState;
  onFilterStateChanged: (filterState: PriceGridFilterState) => void;
  ladderType: PriceGridLadderType;
  priceIncrement: string;
  minPriceIncrement: string;
  onLadderTypeChange: (ladderType: PriceGridLadderType) => void;
  onPriceIncrementChange: (increment: string) => void;
}

export function GenericPriceGridHeader({
  filtersPortalId,
  filterState,
  onFilterStateChanged,
  ladderType,
  priceIncrement,
  minPriceIncrement,
  onLadderTypeChange,
  onPriceIncrementChange,
}: GenericPriceGridHeaderProps) {
  const { setPortalRef: filtersPortalRef } = usePortal(filtersPortalId);
  const filterControls = usePriceGridFilterControls({ filterState, onFilterStateChanged });
  const { orderPresetID, subAccount } = filterState;

  return (
    <PriceGridHeaderContainer data-testid="price-grid-header">
      <PriceGridHeaderFilters>
        <Text color="colorTextImportant" fontWeight={500} whiteSpace="nowrap" flex="0 0 auto">
          Price Grid
        </Text>
        <PriceGridLadderControls
          ladderType={ladderType}
          priceIncrement={priceIncrement}
          minPriceIncrement={minPriceIncrement}
          onLadderTypeChange={onLadderTypeChange}
          onPriceIncrementChange={onPriceIncrementChange}
        />
        <Divider orientation="vertical" my="spacing8" />
        <PriceGridFavoriteFilterButton filterState={filterState} onFilterStateChanged={onFilterStateChanged} />
        <PriceGridProductTypeFilter filterState={filterState} onFilterStateChanged={onFilterStateChanged} />
        <PriceGridSymbolFilters controls={filterControls} />
        <Box ref={filtersPortalRef} />
      </PriceGridHeaderFilters>
      <PriceGridHeaderActions>
        <PriceGridPrimingControls
          orderPresetID={orderPresetID}
          subAccount={subAccount}
          onOrderPresetChange={nextOrderPresetID =>
            onFilterStateChanged({
              ...filterState,
              orderPresetID: nextOrderPresetID,
            })
          }
          onSubAccountChange={nextSubAccount =>
            onFilterStateChanged({
              ...filterState,
              subAccount: nextSubAccount,
            })
          }
        />
      </PriceGridHeaderActions>
    </PriceGridHeaderContainer>
  );
}
